
// components/notifications/NotificationDrawer.tsx
"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { BottomDrawer, AvatarDisplay, Loader, Button } from "../common";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  fetchNotifications,
  fetchUnreadCount,
  markRead,
  markAllRead,
} from "../../redux/notifications/notificationsSlice";
import {
  selectNotifications,
  selectNotificationsLoading,
  selectNotificationsUnread,
  selectNotificationsHasMore,
  selectNotificationsPage,
} from "../../redux/selectors/notifications/notificationsSelectors";
import { openNotificationLink } from "../../utils/notifications/deepLink";
import { getTypeIcon } from "../../utils/notifications/getTypeIcon";
import "./NotificationDrawer.css";

// --- Simple relative time helper ---
function relTime(iso: string) {
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  if (diff < 60000) return "Now";
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

type Props = {
  open: boolean;
  onClose: () => void;
  token: string | null;
};

const NotificationDrawer: React.FC<Props> = ({ open, onClose, token }) => {
  const dispatch = useAppDispatch();
  const router = useRouter();

  // --- Redux selectors ---
  const items = useAppSelector(selectNotifications);
  const loading = useAppSelector(selectNotificationsLoading);
  const unread = useAppSelector(selectNotificationsUnread);
  const hasMore = useAppSelector(selectNotificationsHasMore);
  const page = useAppSelector(selectNotificationsPage);

  // --- Ref برای لیست جهت اسکرول ---
  const listRef = useRef<HTMLUListElement | null>(null);

  // --- First page + WS init when drawer opens ---
  useEffect(() => {
    if (!open || !token) return;

    // First page only if nothing loaded yet
    if (page === 0 && items.length === 0 && !loading) {
      dispatch(fetchNotifications({ page: 1 }));
    }

    // Always refresh unread count
    dispatch(fetchUnreadCount());

  }, [open, token, page, items.length, loading, dispatch]);

  // --- Infinite scroll handler ---
  const handleScroll: React.UIEventHandler<HTMLUListElement> = (e) => {
    const el = e.currentTarget;

    // Do nothing if already loading or no more pages
    if (loading || !hasMore) return;

    const scrollBottom = el.scrollTop + el.clientHeight;
    const threshold = el.scrollHeight - 80; // 80px from bottom

    // If user scrolled near bottom → load next page
    if (scrollBottom >= threshold) {
      const nextPage = page > 0 ? page + 1 : 1;
      dispatch(fetchNotifications({ page: nextPage }));
    }
  };

  // --- Sort newest first (just in case) ---
  const list = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [items]
  );

  // --- Refresh button handler ---
  const handleRefresh = () => {
    // Reset to first page
    dispatch(fetchNotifications({ page: 1 }));
    dispatch(fetchUnreadCount());
  };

  // --- Mark all read handler ---
  const handleMarkAllRead = () => {
    dispatch(markAllRead());
  };

  // -------------------------------------------------------
  // MAIN Notification click handler
  // -------------------------------------------------------
  const handleOpen = async (n: any) => {
    // Mark read (ignore error)
    try {
      await dispatch(markRead(n.id)).unwrap();
    } catch (err) {
    }
    onClose();

    // ---------------------------------------------------
    // CASE 1 — Comment-based notifications
    // ---------------------------------------------------
    if (
      n.notification_type === "comment" ||
      n.notification_type === "comment_reply" ||
      n.content_type === "posts.comment"
    ) {
      const {
        content_type,
        object_id,
        target_comment_id,
        parent_comment_id,
      } = n;

      // 👇 فقط اطلاعات فوکوس را ذخیره می‌کنیم.
      // لاجیک jumpToComment در کامپوننت CommentsThread انجام می‌شود.
      if (target_comment_id) {
        sessionStorage.setItem(
          "focus_comment_id",
          String(target_comment_id)
        );
      }
      if (parent_comment_id) {
        sessionStorage.setItem(
          "focus_parent_id",
          String(parent_comment_id)
        );
      }
      if (content_type) {
        sessionStorage.setItem("focus_content_type", String(content_type));
      }
      if (object_id) {
        sessionStorage.setItem("focus_object_id", String(object_id));
      }

      if (n.link) {
        await openNotificationLink(router, n.link);
      }
      return;
    }

    // ---------------------------------------------------
    // CASE 2 — All other notifications
    // ---------------------------------------------------
    if (n.link) {
      await openNotificationLink(router, n.link);
    }
  };

  // -------------------------------------------------------
  // Footer buttons
  // -------------------------------------------------------
  const footer = (
    <div className="notif-footer">
      {/* Refresh button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleRefresh}
        aria-label="Refresh notifications"
      >
        Refresh
      </Button>

      {/* Mark all read button */}
      <Button
        variant="primary"
        size="sm"
        onClick={handleMarkAllRead}
        aria-label="Mark all as read"
      >
        Mark all read
      </Button>
    </div>
  );

  return (
    <BottomDrawer
      open={open}
      onClose={onClose}
      title={`Notifications ${unread > 0 ? `(${unread})` : ""}`}
      footer={footer}
      maxHeight="80vh"
      showCloseButton
    >
      {/* Initial loader when nothing loaded yet */}
      {loading && list.length === 0 && (
        <Loader
          size="md"
          variant="dots"
          color="glory"
          horizontal="center"
          vertical="center"
          direction="row"
        />
      )}

      {!loading && list.length === 0 && (
        <div className="notif-empty">No notifications yet</div>
      )}

      {/* Scrollable list with infinite scroll */}
      {list.length > 0 && (
        <ul
          className="notif-list"
          ref={listRef}
          onScroll={handleScroll}
        >
          {list.map((n) => {
            const actor = n.actor;

            return (
              <li
                key={n.id}
                className={`notif-item ${n.is_read ? "read" : "unread"}`}
                onClick={() => handleOpen(n)}
                role="button"
              >
                {/* --- Avatar --- */}
                <AvatarDisplay
                  userId={actor?.id ?? undefined}
                  src={actor?.avatar_cdn_url || null}
                  placeholderSrc={actor?.avatar_url || actor?.profile_image || null}
                  alt={actor?.username ?? "User"}
                  size="md"
                  shape="rounded"
                  withBorder={true}
                  borderColor={actor?.label_color ? actor.label_color : undefined}
                  isVerifiedIdentity={actor?.is_verified_identity}
                  isTownlitVerified={actor?.is_townlit_verified}
                  title={actor?.username ?? ""}
                />

                {/* ------------------------------------------------------ */}
                <div className="notif-info-box">
                  <div
                    className="notif-icon-wrap"
                    aria-label={n.notification_type}
                  >
                    {getTypeIcon(n.notification_type)}
                  </div>

                  <div className="notif-meta">
                    <span className="notif-time">{relTime(n.created_at)}</span>
                  </div>
                </div>

                {/* --- Body --- */}
                <div className="notif-body">
                  <div className="notif-msg">
                    {n.message}
                  </div>
                </div>

                {!n.is_read && <div className="notif-unread-dot" />}
              </li>
            );
          })}

          {/* Loader at bottom while fetching next page */}
          {loading && list.length > 0 && (
            <li className="notif-loading-more">
              <Loader
                size="sm"
                variant="dots"
                color="glory"
                horizontal="center"
                vertical="center"
                direction="row"
              />
            </li>
          )}
        </ul>
      )}
    </BottomDrawer>
  );
};

export default NotificationDrawer;
